import styled from '@emotion/styled'
import { useAtom } from 'jotai'
import mapboxgl from 'mapbox-gl'
import { FC, useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { createRoot } from 'react-dom/client'
import { MapTypeToggle } from 'src/components/map/google-map/map-type-toggle'
import { MapInner } from 'src/components/map/mapbox-main-map/map-component'
import { MAP_DEFAULT_STYLE } from 'src/components/map/mapbox/config'
import { RED_DOT_PIN } from 'src/constants/mapbox'
import { useMap } from 'src/hooks/use-mapbox-map'
import { useMapboxHelper } from 'src/utils/mapbox-main-map'
import { PARCEL_ACQUISITION_DATA } from 'src/v2/views/owner-reveal/insights/acquisition-history/acquisitions-overview/data-temp'
import { AcquisitionsOverviewMapTooltip } from 'src/v2/views/owner-reveal/insights/acquisition-history/acquisitions-overview/map-tooltip'
import * as atoms from 'src/v2/views/owner-reveal/insights/acquisition-history/acquisitions-overview/temp-atoms'

const MapWrapper = styled.div`
  height: 400px;
  width: 100%;
  overflow: hidden;
  display: flex;
`

const COORDINATES = [
  [-97.70680399088619, 30.311454614722493],
  [-97.71089193579935, 30.31515440888188],
  [-97.70185889623386, 30.315723595585226],
  [-97.71639747451205, 30.31555283992118],
  [-97.71768319912171, 30.307896988617287],
  [-97.7093424728082, 30.31099924578767],
  [-97.71165018364599, 30.314442920852954],
  [-97.72349768647759, 30.2999528636617],
  [-97.72929718447773, 30.313737322737765],
  [-97.7396789340228, 30.295625493650974],
  [-97.747053604933, 30.288515846811066],
  [-97.727053604933, 30.31099924578767],
  [-97.737053604933, 30.288515846811066],
]

const FEATURES = PARCEL_ACQUISITION_DATA.map((d, i) => ({
  type: 'Feature',
  properties: d,
  geometry: {
    type: 'Point',
    coordinates: COORDINATES[i],
  },
}))

const SOURCE_ID = 'ao-source'
const LAYER_ID = 'ao-layer'

const Layer = () => {
  const [selectedItem, setSelectedItem] = useAtom(atoms.selectedItem)
  const map = useMap()
  const { useSource, useLayer } = useMapboxHelper(map)

  const popup = useRef(
    new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      anchor: 'left',
      offset: [12, 24],
    })
  )
  const popupNode = document.createElement('div')
  const root = createRoot(popupNode)

  useSource(SOURCE_ID, {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: FEATURES as any[],
    },
    promoteId: 'id',
  })

  useLayer({
    id: LAYER_ID,
    type: 'symbol',
    source: SOURCE_ID,
    layout: {
      'icon-image': RED_DOT_PIN,
      'icon-size': 0.8,
    },
  })

  useEffect(() => {
    const onClick = (e: mapboxgl.MapLayerMouseEvent) => {
      popup.current?.remove()

      const feature = e.features?.[0]
      if (!feature || !feature.properties) {
        return
      }
      setSelectedItem({
        id: feature.properties.id,
        origin: 'map',
      })
      flushSync(() => root.render(<AcquisitionsOverviewMapTooltip parcel={feature.properties} />))
      popup.current?.setLngLat(e.lngLat).setDOMContent(popupNode).addTo(map)
    }

    map.on('click', LAYER_ID, onClick)
    return () => {
      map.off('click', LAYER_ID, onClick)
    }
  }, [])

  useEffect(() => {
    if (!selectedItem?.id) {
      return
    }
    const feature = FEATURES.find((f) => f.properties.id === selectedItem.id)
    if (!feature) {
      return
    }

    const properties = feature.properties
    const coordinates = feature.geometry.coordinates

    map.flyTo({ center: coordinates as [number, number] })

    popup.current?.remove()
    flushSync(() => root.render(<AcquisitionsOverviewMapTooltip parcel={properties} />))
    popup.current
      ?.setLngLat(coordinates as [number, number])
      .setDOMContent(popupNode)
      .addTo(map)
  }, [selectedItem?.id])

  return null
}

export const AcquisitionsOverviewMap: FC = () => {
  const [mapStyle, setMapStyle] = useState(MAP_DEFAULT_STYLE)

  return (
    <MapWrapper>
      <MapInner
        isStatic
        zoom={14}
        style={mapStyle}
        bounds={{ center: [-97.707413, 30.31322999999999], northEast: [], southWest: [] }}>
        <Layer />
        <MapTypeToggle mapStyle={mapStyle} setMapStyle={setMapStyle} />
      </MapInner>
    </MapWrapper>
  )
}
